import type { InAppNotification, InAppNotificationType } from '../api/types'
import { formatNotificationActorName } from './formatNotificationActorName'
import { mapMarevaRemoteState } from './mapMarevaRemoteState'
import { mapTrackingStatusNotice } from './mapTrackingStatusNotice'

export type NotificationCopyTranslator = (
  key: string,
  options?: Record<string, unknown>
) => string

export type NotificationCopyParts = {
  highlight: string | null
  message: string
}

const getPayloadString = (
  payload: Record<string, unknown>,
  key: string
): string | null => {
  const value = payload[key]
  return typeof value === 'string' ? value : null
}

const getStatusChangedCopy = (
  t: NotificationCopyTranslator,
  payload: Record<string, unknown>,
  actor: string
): NotificationCopyParts => {
  const labelKey = getPayloadString(payload, 'labelKey')
  const from = getPayloadString(payload, 'from')
  const to = getPayloadString(payload, 'to')

  if (labelKey === 'tracking_status') {
    const notice = mapTrackingStatusNotice(to)
    if (notice) {
      return { highlight: null, message: t(`trackingMessage_${notice}`) }
    }
  }

  if (labelKey === 'remote_state') {
    const toStatus = mapMarevaRemoteState(to)
    if (toStatus === 'accepted') {
      return {
        highlight: null,
        message: t('statusMessage_mareva_accepted'),
      }
    }
    if (toStatus === 'processing') {
      return {
        highlight: null,
        message: t('statusMessage_mareva_processing'),
      }
    }
    if (toStatus === 'rejected') {
      return {
        highlight: null,
        message: t('statusMessage_mareva_rejected'),
      }
    }

    const fromStatus = mapMarevaRemoteState(from)
    if (fromStatus === 'unknown' && toStatus === 'unknown') {
      return {
        highlight: actor || t('systemActorCustoms'),
        message: t('types_declaration_status_changed'),
      }
    }
    const fromLabel =
      fromStatus === 'unknown'
        ? (from ?? t('status_unknown'))
        : t(`status_mareva_${fromStatus}`)
    const toLabel =
      toStatus === 'unknown'
        ? (to ?? t('status_unknown'))
        : t(`status_mareva_${toStatus}`)
    return {
      highlight: actor || null,
      message: t('statusChanged_mareva', { from: fromLabel, to: toLabel }),
    }
  }

  return {
    highlight: actor || null,
    message: t('types_declaration_status_changed'),
  }
}

export const getNotificationCopyParts = (
  t: NotificationCopyTranslator,
  notification: Pick<InAppNotification, 'type' | 'payload' | 'actor'>
): NotificationCopyParts => {
  const actor = formatNotificationActorName(notification.actor)

  switch (notification.type as InAppNotificationType) {
    case 'declaration_status_changed':
      return getStatusChangedCopy(t, notification.payload, actor)
    case 'declaration_comment_added': {
      const preview = getPayloadString(notification.payload, 'preview')
      return {
        highlight: actor || null,
        message: preview
          ? t('commentMessage', { preview })
          : t('commentMessage_fallback'),
      }
    }
    case 'declaration_sent_by_collaborator':
      return {
        highlight: actor || null,
        message: t('sentMessage'),
      }
    // Émise par la gateway quand un utilisateur demande à la douane d'invalider la
    // déclaration : l'acteur est ce collègue, pas la douane, et aucun motif n'est transmis.
    case 'declaration_returned_for_correction':
      return actor
        ? { highlight: actor, message: t('invalidationRequestedMessage') }
        : { highlight: null, message: t('invalidationRequestedMessage_fallback') }
    case 'app_update': {
      const version = getPayloadString(notification.payload, 'version')
      return {
        highlight: null,
        message: version
          ? t('types_app_update', { version })
          : t('types_app_update_generic'),
      }
    }
    default:
      return {
        highlight: null,
        message: t('types_unknown'),
      }
  }
}

export const getNotificationCopy = (
  t: NotificationCopyTranslator,
  notification: Pick<InAppNotification, 'type' | 'payload' | 'actor'>
): string => {
  const { highlight, message } = getNotificationCopyParts(t, notification)
  if (highlight) {
    return `${highlight} ${message}`
  }
  return message
}
